import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Content,
  Footer,
  Header,
  Heading,
  Modal,
  Placeholder,
  Secondary,
  Select,
  Stack,
  Text,
  Toolbar,
  ToolbarItem,
} from '@kinesis/bungle';
import type { CatalogLibrary, LibraryOwnership } from '@/types';

// Modal for attaching a new emission factor library to a scenario — a port of
// suit's `add-library-modal`. Given the inventory's catalog plus the set of
// already-attached library ids, narrows to "libraries you may attach but
// haven't yet" and lets the user pick one. Selection fires
// `onSelect(libraryId)`; the parent closes the modal + updates state.
type AddLibraryModalProps = {
  animation?: boolean;
  catalog: CatalogLibrary[];
  // Library ids the scenario already has attached — filtered out of the picker.
  attachedLibraryIds: number[];
  onClose: () => void;
  onSelect: (libraryId: number) => void;
};

const renderOwnership = (ownership: LibraryOwnership): string => {
  switch (ownership.type) {
    case 'global':
      return 'Global library';
    case 'owned-by':
      // Owned-by libraries reach the catalog only when the querying inventory's
      // organisation matches the library's owner, so this is always the user's
      // organisation from their perspective.
      return 'Owned by your organisation';
  }
};

const AddLibraryModal = ({
  animation,
  catalog,
  attachedLibraryIds,
  onClose,
  onSelect,
}: AddLibraryModalProps) => {
  const [selected, setSelected] = useState<number | undefined>();

  // Filter out already-attached libraries. Re-sort defensively — the server
  // already sorts by name, but the UI shouldn't break if that changes.
  const options = useMemo(() => {
    const attached = new Set(attachedLibraryIds);
    return catalog
      .filter((entry) => !attached.has(entry.library))
      .map((entry) => ({
        id: entry.library,
        label: entry.name,
        ownership: entry.ownership,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [catalog, attachedLibraryIds]);

  const selectedEntry = useMemo(
    () => options.find((o) => o.id === selected),
    [options, selected],
  );

  const handleChange = useCallback((value: number) => {
    setSelected(value);
  }, []);

  const handleSubmit = useCallback(() => {
    if (selected != null) {
      onSelect(selected);
    }
  }, [selected, onSelect]);

  return (
    <Modal
      animation={animation}
      aria-label='Add emission factor library'
      onClose={onClose}
      width={560}
    >
      <Header border>
        <Toolbar>
          <ToolbarItem ellipsify sizing='fill-container'>
            <Heading as='h2' size='large'>
              Add an emission factor library
            </Heading>
          </ToolbarItem>
        </Toolbar>
      </Header>
      <Content padding='medium'>
        <Stack space='medium'>
          <Text>
            Pick a library to attach. The library starts at lowest precedence
            and following its latest published version — you can adjust both
            after attaching.
          </Text>
          {options.length === 0 ? (
            <Placeholder>
              Every library available to this inventory is already attached.
            </Placeholder>
          ) : (
            <Stack space='small'>
              <Select
                search
                itemsLabel='libraries'
                label='Emission factor library'
                overridePlaceholder='Select a library'
                onChange={handleChange}
                options={options}
                value={selected}
                valueKey='id'
              />
              {selectedEntry && (
                <Secondary>
                  <Text>{renderOwnership(selectedEntry.ownership)}</Text>
                </Secondary>
              )}
            </Stack>
          )}
        </Stack>
      </Content>
      <Footer border>
        <Toolbar>
          <ToolbarItem>
            <Button onClick={onClose} variant='secondary'>
              Cancel
            </Button>
          </ToolbarItem>
          <ToolbarItem>
            <Button
              onClick={handleSubmit}
              variant='primary'
              disabled={selected == null || options.length === 0}
            >
              Add library
            </Button>
          </ToolbarItem>
        </Toolbar>
      </Footer>
    </Modal>
  );
};

export { AddLibraryModal };
export type { AddLibraryModalProps };
